/**
 * Occurrence-based formula component helpers for the formula editor.
 *
 * Expressions use the component dialect: `{c_<id>}` for persisted
 * components and `{c_tmp_<clientKey>}` for components minted in the
 * current editing session. Editor tokens are `mce-mergetag` spans that
 * carry the token body in `data-component-key`, so serialization walks
 * the DOM instead of parsing display text.
 */
import { ComponentKind } from "~/graphql/supergraphEnums";
import type {
  ComponentKind as ComponentKindValue,
  FormulaComponentInput,
} from "~/graphql/supergraphTypes";

export type ComponentInsertion = {
  kind: ComponentKindValue;
  nodeId?: number;
  formulaId?: number;
  primaryField?: string;
  /** Target's current display name. Primary fields use the raw
   *  `<table>.<column>` key so frontend labels match the API's. */
  targetName: string;
  /** Hierarchy/formula path for secondary text in the components panel. */
  targetPath?: ReadonlyArray<string>;
};

export type DraftComponent = ComponentInsertion & {
  /** Server id — present on components loaded from a saved formula. */
  id?: number;
  /** Client key — present on components minted this session. */
  clientKey?: string;
};

export const mintClientKey = (): string => crypto.randomUUID();

/** Expression-token body for a component: `c_<id>` or `c_tmp_<clientKey>`. */
export const componentKey = (component: DraftComponent): string =>
  component.id != null ? `c_${component.id}` : `c_tmp_${component.clientKey}`;

/** Stable identity of a component's target: `node:<id>`,
 *  `formula:<id>`, or `primary:<field>`. */
export const componentTargetKey = (component: ComponentInsertion): string => {
  switch (component.kind) {
    case ComponentKind.Node:
      return `node:${component.nodeId}`;
    case ComponentKind.Formula:
      return `formula:${component.formulaId}`;
    default:
      return `primary:${component.primaryField}`;
  }
};

/**
 * Derives display labels keyed by component key, mirroring the API: the
 * bare target name for a unique occurrence, `name (rank)` with a 1-based
 * rank in array order when the same target occurs more than once.
 */
export const deriveComponentLabels = (
  components: ReadonlyArray<DraftComponent>,
): Map<string, string> => {
  const occurrencesByTarget = new Map<string, DraftComponent[]>();
  for (const component of components) {
    const targetKey = componentTargetKey(component);
    const group = occurrencesByTarget.get(targetKey);
    if (group) {
      group.push(component);
    } else {
      occurrencesByTarget.set(targetKey, [component]);
    }
  }

  const labels = new Map<string, string>();
  for (const occurrences of occurrencesByTarget.values()) {
    const name = occurrences[0].targetName;
    occurrences.forEach((component, index) => {
      labels.set(
        componentKey(component),
        occurrences.length === 1 ? name : `${name} (${index + 1})`,
      );
    });
  }
  return labels;
};

const TOKEN_PREFIX_BY_KIND: Record<ComponentKindValue, string> = {
  [ComponentKind.Node]: ".Hierarchy.",
  [ComponentKind.Formula]: ".Formula.",
  [ComponentKind.PrimaryField]: ".PrimaryField.",
};

/**
 * Visible text of a component's token, in the three-prefix display
 * dialect the API's `expressionWithLabels` also emits — e.g.
 * `{.Hierarchy.Balance (2).}`. Display-only; never parsed back.
 */
export const tokenDisplayText = (kind: ComponentKindValue, label: string): string =>
  `{${TOKEN_PREFIX_BY_KIND[kind]}${label}.}`;

/** Display texts keyed by component key, for the derived labels. */
export const deriveTokenDisplayTexts = (
  components: ReadonlyArray<DraftComponent>,
): Map<string, string> => {
  const labels = deriveComponentLabels(components);
  const displayTexts = new Map<string, string>();
  for (const component of components) {
    const key = componentKey(component);
    const label = labels.get(key);
    if (label != null) {
      displayTexts.set(key, tokenDisplayText(component.kind, label));
    }
  }
  return displayTexts;
};

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Non-editable token span inserted into the editor for a component. */
export const componentTokenHtml = (component: DraftComponent, label: string): string =>
  `<span class="mce-mergetag" contenteditable="false" data-component-key="${componentKey(
    component,
  )}">${escapeHtml(tokenDisplayText(component.kind, label))}</span>`;

const BLOCK_TAGS = new Set(["P", "DIV"]);

const serializeNode = (node: Node, out: string[], componentKeys: string[]): void => {
  if (node.nodeType === Node.TEXT_NODE) {
    out.push((node as Text).data);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const element = node as Element;
  const key = element.getAttribute("data-component-key");
  if (key) {
    out.push(`{${key}}`);
    componentKeys.push(key);
    return;
  }
  if (element.tagName === "BR") {
    out.push("\n");
    return;
  }
  element.childNodes.forEach((child) => serializeNode(child, out, componentKeys));
  if (BLOCK_TAGS.has(element.tagName)) {
    out.push("\n");
  }
};

/**
 * Serializes editor content to the component-dialect expression plus the
 * component keys present, in token order (duplicates preserved).
 */
export const serializeEditorBody = (
  body: Element,
): { expression: string; componentKeys: string[] } => {
  const out: string[] = [];
  const componentKeys: string[] = [];
  body.childNodes.forEach((child) => serializeNode(child, out, componentKeys));
  const expression = out
    .join("")
    .replace(/\uFEFF/g, "")
    .replace(/\u00a0/g, " ")
    .trim();
  return { expression, componentKeys };
};

/** Builds initial editor HTML from a component-dialect expression. */
export const buildEditorContentHtml = (
  expression: string,
  components: ReadonlyArray<DraftComponent>,
): string => {
  const componentsByKey = new Map(components.map((c) => [componentKey(c), c]));
  const labels = deriveComponentLabels(components);
  return expression.replace(
    /\{(c_\d+|c_tmp_[A-Za-z0-9_-]+)\}|([^{]+|\{)/g,
    (match, tokenKey: string | undefined, text: string | undefined) => {
      if (tokenKey) {
        const component = componentsByKey.get(tokenKey);
        if (component) {
          return componentTokenHtml(component, labels.get(tokenKey) ?? component.targetName);
        }
        return escapeHtml(match);
      }
      return escapeHtml(text ?? "");
    },
  );
};

/**
 * Rewrites token span text to the current display texts. Safe to call on
 * every label change; only touches spans whose text is stale.
 */
export const updateTokenLabels = (root: Element, displayTexts: Map<string, string>): void => {
  root.querySelectorAll("[data-component-key]").forEach((span) => {
    const key = span.getAttribute("data-component-key");
    if (!key) return;
    const text = displayTexts.get(key);
    if (text == null) return;
    if (span.textContent !== text) {
      span.textContent = text;
    }
  });
};

/**
 * Marks the token spans whose keys are in `errorKeys` with
 * `data-component-error` (styled via the editor's `content_style`) and
 * clears the mark everywhere else.
 */
export const markErrorTokens = (root: Element, errorKeys: ReadonlySet<string>): void => {
  root.querySelectorAll("[data-component-key]").forEach((span) => {
    const key = span.getAttribute("data-component-key");
    if (key && errorKeys.has(key)) {
      span.setAttribute("data-component-error", "");
    } else {
      span.removeAttribute("data-component-error");
    }
  });
};

/**
 * Re-mints identity for token spans that share a `data-component-key`,
 * so every visible chip stays its own occurrence. Editor actions that
 * clone spans verbatim (paste, drag-copy, redo) duplicate the key; in
 * document order, the first span with a given key keeps it, and each
 * later span whose key resolves to a component in `components` gets a
 * fresh draft copy of that component's target and its span rewritten to
 * the new key. Spans whose key resolves to nothing are left untouched —
 * validation reports them.
 *
 * Returns the minted drafts for the caller to append to the session
 * component list.
 */
export const ensureUniqueComponentKeys = (
  body: Element,
  components: ReadonlyArray<DraftComponent>,
): DraftComponent[] => {
  const componentsByKey = new Map(components.map((c) => [componentKey(c), c]));
  const seenKeys = new Set<string>();
  const minted: DraftComponent[] = [];
  body.querySelectorAll("[data-component-key]").forEach((span) => {
    const key = span.getAttribute("data-component-key");
    if (!key) return;
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      return;
    }
    const source = componentsByKey.get(key);
    if (!source) return;
    const draft: DraftComponent = {
      kind: source.kind,
      nodeId: source.nodeId,
      formulaId: source.formulaId,
      primaryField: source.primaryField,
      targetName: source.targetName,
      targetPath: source.targetPath,
      clientKey: mintClientKey(),
    };
    span.setAttribute("data-component-key", componentKey(draft));
    minted.push(draft);
  });
  return minted;
};

/**
 * Maps components to the mutation/validation input shape: persisted
 * components serialize to bare `{ id }`; drafts carry clientKey, kind,
 * and target fields.
 */
export const toComponentInputs = (
  components: ReadonlyArray<DraftComponent>,
): FormulaComponentInput[] =>
  components.map((component) =>
    component.id != null
      ? { id: component.id }
      : {
          clientKey: component.clientKey,
          componentKind: component.kind,
          referencedNodeId: component.nodeId ?? null,
          referencedFormulaId: component.formulaId ?? null,
          referencedPrimaryField: component.primaryField ?? null,
        },
  );

/**
 * Narrows the session's component list to the occurrences whose tokens are
 * present in the editor, preserving insertion order. The full list is kept
 * in state so undo can restore a deleted token's identity.
 */
export const filterActiveComponents = (
  components: ReadonlyArray<DraftComponent>,
  activeKeys: ReadonlyArray<string>,
): DraftComponent[] => {
  const active = new Set(activeKeys);
  return components.filter((component) => active.has(componentKey(component)));
};

/** Shape of `FormulaDetail.components` rows used for rehydration. */
export type DetailComponent = {
  id: number;
  componentKind: ComponentKindValue;
  referencedNodeId?: number | null;
  referencedFormulaId?: number | null;
  referencedPrimaryField?: string | null;
  sortOrder: number;
  label: string;
};

/**
 * Rehydrates saved components into DraftComponents, ordered by sortOrder.
 * Target names resolve from the provided lookups (current names, so labels
 * track renames); the API label with any rank suffix stripped is the
 * fallback for targets no longer resolvable.
 */
export const fromDetailComponents = (
  detailComponents: ReadonlyArray<DetailComponent>,
  lookups: {
    nodeNameById: ReadonlyMap<number, string>;
    nodePathById: ReadonlyMap<number, ReadonlyArray<string>>;
    formulaNameById: ReadonlyMap<number, string>;
  },
): DraftComponent[] =>
  [...detailComponents]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((component) => {
      const fallbackName = component.label.replace(/ \(\d+\)$/, "");
      let targetName = fallbackName;
      let targetPath: ReadonlyArray<string> | undefined;
      if (
        component.componentKind === ComponentKind.Node &&
        component.referencedNodeId != null
      ) {
        targetName = lookups.nodeNameById.get(component.referencedNodeId) ?? fallbackName;
        targetPath = lookups.nodePathById.get(component.referencedNodeId);
      } else if (
        component.componentKind === ComponentKind.Formula &&
        component.referencedFormulaId != null
      ) {
        targetName = lookups.formulaNameById.get(component.referencedFormulaId) ?? fallbackName;
      } else if (component.componentKind === ComponentKind.PrimaryField) {
        targetName = component.referencedPrimaryField ?? fallbackName;
      }
      return {
        id: component.id,
        kind: component.componentKind,
        nodeId: component.referencedNodeId ?? undefined,
        formulaId: component.referencedFormulaId ?? undefined,
        primaryField: component.referencedPrimaryField ?? undefined,
        targetName,
        targetPath,
      };
    });
